<template>
  <v-container>
    <v-row
      justify="space-between"
      align="center"
    >
      <v-col
        cols="12"
        class="text-center"
      >
        <h1 class="text-h1 text-uppercase font-weight-medium mb-6">
          {{ $t('pages.curtainPage.title') }}
        </h1>
        <p class="mb-10">
          {{ $t('pages.curtainPage.subtitle') }}
        </p>
        <v-divider />
      </v-col>
    </v-row>
    <v-row>
      <v-container>
        <stepper
          ref="stepper"
          data-test="flowStepper"
          :steps="steps"
          @change="onChangeStep"
        >
          <v-stepper-content step="1">
            <v-row>
              <h1 class="pageTitle pageTitle_absolute text-uppercase text-h3">
                {{ $t('pages.curtainPage.steps.fabric.title') }}
              </h1>
              <v-col cols="12">
                <fabric-picker
                  ref="fabricPicker"
                  :multiple="false"
                  @next="nextFabric"
                  @pickFabric="handlePickFabric"
                />
              </v-col>
            </v-row>
          </v-stepper-content>
          <v-stepper-content step="2">
            <v-row>
              <h1 class="pageTitle pageTitle_absolute text-uppercase text-h3">
                {{ $t('pages.curtainPage.steps.headerType.title') }}
              </h1>
              <curtain-picker
                @pickCurtain="nextCurtain"
                @back="previous"
              />
            </v-row>
          </v-stepper-content>
          <v-stepper-content step="3">
            <v-row>
              <h1 class="pageTitle pageTitle_absolute text-uppercase text-h3">
                {{ $t(`pages.curtainPage.steps.${accessoryTitle}.title`) }}
              </h1>
              <curtain-flow
                ref="curtainFlow"
                @next="nextCurtainFlow"
                @previous="previous"
              />
            </v-row>
          </v-stepper-content>
          <v-stepper-content step="4">
            <v-row>
              <h1 class="pageTitle pageTitle_absolute text-uppercase text-h3">
                {{ $t('pages.curtainPage.steps.curtainDrop.title') }}
              </h1>
              <curtain-drop-picker
                @next="nextCurtainDrop"
                @back="previous"
              />
            </v-row>
          </v-stepper-content>
          <v-stepper-content
            complete
            step="5"
          >
            <v-row>
              <h1 class="pageTitle pageTitle_absolute text-uppercase text-h3">
                {{ $t('pages.curtainPage.steps.roomLocation.title') }}
              </h1>

              <room-location-picker
                @pickLocation="next"
                @back="previous"
                @restart="restart"
                @addToCart="addToCart"
                @next="next"
              />
            </v-row>
          </v-stepper-content>
        </stepper>
      </v-container>
    </v-row>
  </v-container>
</template>
<script>
const Stepper = () => import('@/components/Steppers/Stepper')
const FabricPicker = () => import('@/components/Pickers/FabricPicker')
const CurtainPicker = () => import('@/components/Pickers/CurtainPicker')
const CurtainFlow = () => import('@/components/Flows/CurtainFlow')
const CurtainDropPicker = () => import('@/components/Pickers/CurtainDropPicker')
const RoomLocationPicker = () =>
  import('@/components/Pickers/RoomLocationPicker')
import OrderOperator from '@/mixins/Order/OrderOperator'
import { FetchCurtain } from '@/mixins/Curtain/FetchCurtain'
import { FetchRailRod } from '@/mixins/Curtain/FetchRailRod'
import IframeIntegration from '@/mixins/IframeIntegration'

export default {
  name: 'CurtainConfigurator',
  metaInfo: {
    title: 'Curtain Configurator | Spotlight Vadain'
  },
  components: {
    Stepper,
    FabricPicker,
    CurtainPicker,
    CurtainFlow,
    CurtainDropPicker,
    RoomLocationPicker
  },
  mixins: [FetchCurtain, FetchRailRod, OrderOperator, IframeIntegration],
  computed: {
    continueOrder() {
      return this.$store.getters['order/submitButton/getContinueOrder']
    },
    accessoryTitle() {
      return this.$store.getters['order/getRailRodType']
    },
    getRailRodParameters() {
      return this.$store.getters['order/getRailRodParameters']
    },
    getCurtainParameters() {
      return this.$store.getters['order/getCurtainParameters']
    },
    submitButton() {
      return this.$store.getters['order/submitButton/getButton']
    },
    steps() {
      return [
        this.$i18n.t('pages.curtainPage.steps.fabric.header'),
        this.$i18n.t('pages.curtainPage.steps.headerType.header'),
        this.$i18n.t('pages.curtainPage.steps.railRod.header'),
        this.$i18n.t(`pages.curtainPage.steps.curtainDrop.header`),
        this.$i18n.t('pages.curtainPage.steps.roomLocation.header')
      ]
    }
  },
  mounted() {
    this.resetOrder()
  },
  methods: {
    onChangeStep(step) {
      if (this.$refs.stepper) this.$vuetify.goTo(this.$refs.stepper)
    },
    addToCart() {
      const cartData = this.$store.getters['order/getCartData']
      this.$store.commit('order/submitButton/startLoader')
      if (this.$gtm?.enabled()) {
        window.dataLayer?.push({
          event: 'add_to_cart',
          curtain_height: this.curtainParameters?.height,
          curtain_hem_type: this.curtainParameters?.hem_type,
          curtain_luxury: this.curtainParameters?.luxury,
          curtain_main_fabric: this.curtainParameters?.main_fabric,
          curtain_pleat_percentage: this.curtainParameters?.pleat_percentage,
          curtain_ring_size: this.curtainParameters?.ring_size,
          curtain_ring_type: this.curtainParameters?.ring_type,
          curtain_width: this.curtainParameters?.width,
          rail_rod_color: this.getRailRodParameters?.color,
          rail_rod_length: this.getRailRodParameters?.length,
          rail_rod_width: this.getRailRodParameters?.width
        });
      }
      this.sendMessageToSpotlight('addToCart', cartData)
    },
    next() {
      this.$refs.stepper.next()
    },
    nextFabric(fabric) {
      this.next()
      if (this.$gtm?.enabled()) {
        window.dataLayer?.push({
          event: 'select_fabric',
          fabric: fabric
        });
      }
    },
    nextCurtain(headerType) {
      this.next()
      if (this.$gtm?.enabled()) {
        window.dataLayer?.push({
          event: 'select_header_type',
          header_type: headerType
        });
      }
    },
    nextCurtainFlow() {
      this.next()
      if (this.$gtm?.enabled()) {
        window.dataLayer?.push({
          event: 'select_rail_rod',
          rail_rod_color: this.getRailRodParameters?.color,
          rail_rod_length: this.getRailRodParameters?.length,
          rail_rod_width: this.getRailRodParameters?.width
        });
      }
    },
    nextCurtainDrop() {
      this.next()
      if (this.$gtm?.enabled()) {
        window.dataLayer?.push({
          event: 'select_curtain_drop',
          curtain_width: this.curtainParameters?.width,
          curtain_drop: this.curtainParameters?.height
        });
      }
    },
    previous() {
      this.$refs.stepper.previous()
    },
    restart() {
      this.$refs.stepper.restart()
    },
    handlePickFabric(fabric) {
      if (this.$gtm?.enabled()) {
        window.dataLayer?.push({
          event: 'fabric_pick',
          fabric_pick: fabric
        });
      }
    }
  }
}
</script>
<style></style>
